import { CustomCode, CustomCodeReference } from '@betterfunnel/types';
import AlertModal from '@betterfunnel/ui-core/AlertModal';
import Button from '@betterfunnel/ui-core/Button';
import {
  getCustomCodeSource,
  getEffectiveCustomCodes,
  isCustomCodeOverridden,
  toggleCustomCodeReference,
  updateCustomCode,
} from '@betterfunnel/utils';
import { PlusIcon } from '@heroicons/react/24/outline';
import { useCallback, useContext, useMemo, useState } from 'react';
import CustomCodeCard from '../../../components/CustomCodes/CustomCodeCard';
import CustomCodeEditorModal from '../../../components/CustomCodes/CustomCodeEditorModal';
import SettingSectionTitle from '../../../components/settings/SettingSectionTitle';
import { DashboardContext } from '../../../contexts/DashboardContext';
import { SnackbarContext } from '../../../contexts/SnackbarContext';
import { UserContext } from '../../../contexts/UserContext';
import { updateDynamicPage } from '../../../services/DynamicPagesService';

export const ManageDynamicPageCustomCodes = () => {
  const { show } = useContext(SnackbarContext);
  const { selectedAccount } = useContext(UserContext);
  const { selectedDynamicPage, accountConfig } = useContext(DashboardContext);

  const [editorOpen, setEditorOpen] = useState(false);
  const [editingCode, setEditingCode] = useState<CustomCode | undefined>();
  const [deleteConfirm, setDeleteConfirm] = useState<string | undefined>();

  const campaignConfig = selectedDynamicPage?.data().config;

  const accountCodes = useMemo(
    () => accountConfig?.customCodes || [],
    [accountConfig?.customCodes],
  );
  const campaignCodes = useMemo(
    () => campaignConfig?.customCodes || [],
    [campaignConfig?.customCodes],
  );
  const campaignReferences = useMemo(
    () => campaignConfig?.customCodesReferences || [],
    [campaignConfig?.customCodesReferences],
  );

  const effectiveCodes = getEffectiveCustomCodes(
    accountCodes,
    campaignCodes,
    undefined,
    campaignReferences,
    undefined,
  );

  const updateCampaignConfig = useCallback(
    async (updates: {
      customCodes?: CustomCode[];
      customCodesReferences?: CustomCodeReference[];
    }) => {
      if (!selectedAccount || !selectedDynamicPage) return;
      try {
        await updateDynamicPage(selectedAccount.id, selectedDynamicPage.id, {
          config: {
            ...campaignConfig,
            ...updates,
          } as any,
        });
        show('Updated');
      } catch (error) {
        console.error(error);
        show('Failed to update');
      }
    },
    [selectedAccount, selectedDynamicPage, campaignConfig, show],
  );

  const handleAddCode = () => {
    setEditingCode(undefined);
    setEditorOpen(true);
  };

  const handleEditCode = (code: CustomCode) => {
    setEditingCode(code);
    setEditorOpen(true);
  };

  const handleSaveCode = useCallback(
    async (code: CustomCode) => {
      const isNew = !campaignCodes.find((c) => c.id === code.id);

      if (isNew) {
        // Add new code
        await updateCampaignConfig({
          customCodes: [...campaignCodes, code],
          customCodesReferences: campaignReferences,
        });
      } else {
        // Update existing code
        const updatedCodes = campaignCodes.map((c) =>
          c.id === code.id ? code : c,
        );
        await updateCampaignConfig({
          customCodes: updatedCodes,
          customCodesReferences: campaignReferences,
        });
      }
    },
    [campaignCodes, campaignReferences, updateCampaignConfig],
  );

  const handleToggleCode = useCallback(
    async (codeId: string, enabled: boolean) => {
      const source = getCustomCodeSource(codeId, accountCodes, campaignCodes);

      if (source === 'campaign') {
        // Toggle campaign code directly
        const updatedCodes = campaignCodes.map((c) =>
          c.id === codeId ? updateCustomCode(c, { enabled }) : c,
        );
        await updateCampaignConfig({
          customCodes: updatedCodes,
          customCodesReferences: campaignReferences,
        });
      } else if (source === 'account') {
        // Toggle account code via reference
        const updatedRefs = toggleCustomCodeReference(
          codeId,
          campaignReferences,
          enabled,
        );
        await updateCampaignConfig({
          customCodes: campaignCodes,
          customCodesReferences: updatedRefs,
        });
      }
    },
    [accountCodes, campaignCodes, campaignReferences, updateCampaignConfig],
  );

  const handleDeleteCode = useCallback(
    async (codeId: string) => {
      const updatedCodes = campaignCodes.filter((c) => c.id !== codeId);
      await updateCampaignConfig({
        customCodes: updatedCodes,
        customCodesReferences: campaignReferences,
      });
      setDeleteConfirm(undefined);
    },
    [campaignCodes, campaignReferences, updateCampaignConfig],
  );

  // Group codes by source for better organization
  const groupedCodes = {
    account: effectiveCodes.filter((code) =>
      accountCodes.some((c) => c.id === code.id),
    ),
    campaign: effectiveCodes.filter((code) =>
      campaignCodes.some((c) => c.id === code.id),
    ),
  };

  return (
    <div>
      <SettingSectionTitle
        title="Custom Codes (Advanced)"
        description="Manage multiple custom code snippets. Account-level codes are inherited and can be toggled on/off."
      />

      <div className="mt-6 border-t border-gray-200 pt-6">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-sm font-medium text-gray-900">
            Campaign Custom Codes
          </h3>
          <Button size="small" onClick={handleAddCode}>
            <PlusIcon className="-ml-0.5 h-4 w-4" />
            Add Code
          </Button>
        </div>

        {groupedCodes.campaign.length === 0 &&
        groupedCodes.account.length === 0 ? (
          <div className="rounded-lg border-2 border-dashed border-gray-300 p-12 text-center">
            <p className="text-sm text-gray-500">
              No custom codes yet. Add your first custom code to get started.
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Campaign-specific codes */}
            {groupedCodes.campaign.length > 0 && (
              <div className="space-y-3">
                {groupedCodes.campaign.map((code) => {
                  // Campaign codes are never overridden (they're the source)
                  const isOverridden = false;
                  return (
                    <CustomCodeCard
                      key={code.id}
                      customCode={code}
                      source="campaign"
                      isOverridden={isOverridden}
                      displayEnabled={code.enabled}
                      onToggle={handleToggleCode}
                      onEdit={handleEditCode}
                      onDelete={(id) => setDeleteConfirm(id)}
                    />
                  );
                })}
              </div>
            )}

            {/* Inherited account codes */}
            {groupedCodes.account.length > 0 && (
              <div className="space-y-3">
                <h4 className="text-sm font-medium text-gray-700">
                  Inherited from Account
                </h4>
                {groupedCodes.account.map((code) => {
                  // Find the original account code to get its original enabled state
                  const originalCode = accountCodes.find(
                    (c) => c.id === code.id,
                  );
                  const isOverridden = isCustomCodeOverridden(
                    code.id,
                    originalCode?.enabled ?? true,
                    campaignReferences,
                  );
                  return (
                    <CustomCodeCard
                      key={code.id}
                      customCode={code}
                      source="account"
                      isInherited
                      isOverridden={isOverridden}
                      displayEnabled={code.enabled}
                      onToggle={handleToggleCode}
                      readOnly
                    />
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      <CustomCodeEditorModal
        customCode={editingCode}
        open={editorOpen}
        onClose={() => {
          setEditorOpen(false);
          setEditingCode(undefined);
        }}
        onSave={handleSaveCode}
        userId={selectedAccount?.data().createdBy || 'unknown'}
      />

      <AlertModal
        title="Delete Custom Code"
        open={!!deleteConfirm}
        onClose={() => setDeleteConfirm(undefined)}
        actions={[
          {
            type: 'secondary',
            title: 'Cancel',
            onClick: () => setDeleteConfirm(undefined),
          },
          {
            type: 'primary',
            color: 'negative',
            title: 'Delete',
            onClick: () => deleteConfirm && handleDeleteCode(deleteConfirm),
          },
        ]}
        type="warn"
      >
        <p>
          Are you sure you want to delete this custom code? This action cannot
          be undone.
        </p>
      </AlertModal>
    </div>
  );
};
